// src/features/system/archiver/hooks/useArchiver.ts
// ────────────────────────────────────────────────────────────────────────────
// Archiver — Hook Owns All State.
//
// Fetches archive pipeline status + summary data. Provides mutations for
// manual trigger. Components are pure renderers.
// ────────────────────────────────────────────────────────────────────────────
import { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ARCHIVER_KEYS,
  fetchArchiveStatus,
  fetchArchiveSummary,
  triggerArchiveNow,
  type ArchiveStatusResponse,
  type ArchiveSummaryResponse,
  type ArchiveSummaryRow,
} from '../api/archiverApi';

type Granularity = 'daily' | 'weekly' | 'monthly';

export function useArchiver() {
  const qc = useQueryClient();
  const [granularity, setGranularity] = useState<Granularity>('daily');

  // ── Pipeline status ────────────────────────────────────────────────────
  const status = useQuery<ArchiveStatusResponse, Error>({
    queryKey: ARCHIVER_KEYS.status(),
    queryFn: fetchArchiveStatus,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  // ── Archive summary (daily/weekly/monthly) ─────────────────────────────
  const summary = useQuery<ArchiveSummaryResponse, Error>({
    queryKey: ARCHIVER_KEYS.summary(granularity),
    queryFn: () => fetchArchiveSummary(granularity),
    staleTime: 30_000,
  });

  // ── Manual trigger ─────────────────────────────────────────────────────
  const trigger = useMutation({
    mutationFn: triggerArchiveNow,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ARCHIVER_KEYS.all });
    },
  });

  // ── Derived: namespace breakdown ───────────────────────────────────────
  const namespaceBreakdown = useMemo(() => {
    const rows = summary.data?.summary ?? [];
    const map = new Map<string, { rows: number; bytes: number; files: number }>();
    for (const r of rows) {
      const existing = map.get(r.scope_ns) ?? { rows: 0, bytes: 0, files: 0 };
      existing.rows += r.total_rows;
      existing.bytes += r.parquet_bytes;
      existing.files += r.parquet_files;
      map.set(r.scope_ns, existing);
    }
    return Array.from(map.entries())
      .map(([ns, v]) => ({ namespace: ns, ...v }))
      .sort((a, b) => b.rows - a.rows);
  }, [summary.data]);

  // ── Derived: daily timeline for chart ──────────────────────────────────
  const dailyTimeline = useMemo(() => {
    if (granularity !== 'daily') return [];
    const rows = summary.data?.summary ?? [];
    const dateMap = new Map<string, number>();
    for (const r of rows) {
      dateMap.set(r.period_start, (dateMap.get(r.period_start) ?? 0) + r.total_rows);
    }
    return Array.from(dateMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count }));
  }, [summary.data, granularity]);

  // ── Derived: partition timeline ────────────────────────────────────────
  const partitionTimeline = useMemo(() => {
    const partitions = status.data?.partitions ?? [];
    return partitions.map(p => ({
      date: p.date?.replace(/_/g, '-') ?? p.name,
      rows: p.rows,
      sizeBytes: p.size_bytes,
      size: p.size,
      name: p.name,
    }));
  }, [status.data]);

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ARCHIVER_KEYS.all });
  }, [qc]);

  return {
    // Status data
    status: status.data,
    statusLoading: status.isLoading,
    statusError: status.isError,
    statusUpdatedAt: status.dataUpdatedAt,

    // Summary data
    summary: summary.data,
    summaryLoading: summary.isLoading,

    // Derived
    namespaceBreakdown,
    dailyTimeline,
    partitionTimeline,

    // Controls
    granularity,
    setGranularity,
    refresh,

    // Trigger
    triggerArchive: trigger.mutate,
    triggerPending: trigger.isPending,
  };
}
